import React, { useState, useEffect, useRef } from 'react';
import Styles from './styles.module.scss';
import { useTranslation } from 'react-i18next';
import Seo from '../../components/_UI/seo';

import { useAccount, useDisconnect, useSignMessage } from 'wagmi';
import { useConnectModal } from '@rainbow-me/rainbowkit';
import { useDispatch, useSelector } from 'react-redux';
import {getNonce, login, loginWithHonee} from '../../store/main/action';
import {useNavigate, useParams} from "react-router-dom";
import {getUserData, setUserName, setUserNameFailure} from "../../store/cabinet/action";

import honeeLogo from '../../theme/assets/svg/honee_logo.svg';

const RegPage = () => {
    const { t } = useTranslation();
    const { address, isConnected } = useAccount();
    const { disconnect } = useDisconnect();
    const { openConnectModal } = useConnectModal();
    const dispatch = useDispatch();
    const { signMessageAsync } = useSignMessage();
    const ignoreNextEffect = useRef(false);
    const isNonceSigned = useRef(false);
    const navigate = useNavigate();
    const {code} = useParams();

    const nonce = useSelector(store => store.main.nonce);
    const userData = useSelector(store => store.cabinet.userData);
    const authStatus = useSelector(store => store.main.authorizationStatus);
    const userNameErr = useSelector(store => store.cabinet.userNameErr);


    const [currentStep, setCurrentStep] = useState(1);
    const [walletAddress, setWalletAddress] = useState(null);
    const [name, setName] = useState('');

    const handleDisconnect = () => {
        ignoreNextEffect.current = true;
        disconnect();
        setWalletAddress(null);
        isNonceSigned.current = false;
    };

    const honeeLoginHandler = () =>{
        window.location.assign(`${process.env.REACT_APP_HONEE_URL}/sign-in/${process.env.REACT_APP_CLIENT_ID}`);
    }

    useEffect(()=>{
        if (authStatus){
            navigate('/launchpad');
        }
    },[authStatus, navigate])


    useEffect(()=>{
        if (code){
            dispatch(loginWithHonee({token: code}))
        }
    },[code, dispatch])

    useEffect(() => {
        if (walletAddress || code){
            if (userData?.user?.wallet){
                navigate('/launchpad');
            }
        }
    }, [navigate, userData, walletAddress, code]);


    useEffect(() => {
        if (ignoreNextEffect.current) {
            ignoreNextEffect.current = false;
            return;
        }

        if (isConnected && address && !walletAddress) {
            setWalletAddress(address);
            dispatch(getNonce({wallet: address}));
            isNonceSigned.current = false;
        }
    }, [isConnected, address, dispatch, walletAddress]);


    useEffect(() => {
        const signNonce = async () => {
            if (!isNonceSigned.current && nonce && walletAddress) {
                try {
                    isNonceSigned.current = true;
                    const signature = await signMessageAsync({ message: nonce });
                    if (signature) {
                        dispatch(login({
                            wallet: walletAddress,
                            nonce: nonce,
                            signed_message: signature
                        }));
                    }
                } catch (err) {
                    console.error('🚫 Signature error:', err);
                    isNonceSigned.current = false;
                }
            }
        };

        signNonce();
    }, [nonce, walletAddress, signMessageAsync, dispatch]);

    useEffect(()=>{
        dispatch(getUserData());
    }, [dispatch])


    return (
        <section className={`${Styles.container} ${Styles.pageContainer}`}>
            <Seo title={t('Вход')} />
            <div className={Styles.contentWrap}>
                {code ?
                    <div className={Styles.loaderWrap}>
                        <div className={Styles.loaderTitle}>{t('Вход с помощью')}</div>
                        <div className={Styles.loaderLogoWrap}>
                            <img src={honeeLogo} alt="honeeai"/>
                        </div>
                        <div className={Styles.loaderNote}>{t('Загрузка...')}</div>
                    </div> :
                    <div className={Styles.contentBox}>
                        <div className={Styles.stepRow}>
                            <div className={`${Styles.stepRowItem} ${Styles.stepRowItemActive}`} />
                            <div className={`${Styles.stepRowItem} ${currentStep > 1 ? Styles.stepRowItemActive : ''}`} />
                        </div>

                        {currentStep === 1 && (
                            <div className={`${Styles.stepWrap} ${Styles.step1}`}>
                                <div className={Styles.stepWrapTitle}>{t('Готовы подключиться')}?</div>
                                <div className={Styles.stepWrapNote}>{t('Подключите свой кошелёк, чтобы начать')}</div>

                                <div className={Styles.stepWalletWrap}>
                                    <div
                                        className={`${Styles.stepWalletItem} ${walletAddress ? Styles.stepWalletItemDsb : null}`}
                                        onClick={()=>{
                                            if (!walletAddress){
                                                openConnectModal();
                                            }
                                        }}>
                                        {t('Подключить кошелек')}
                                    </div>
                                </div>

                                <div className={Styles.stepWalletNote}>
                                    <div className={Styles.stepWalletNoteTitleWrap}>
                                        <div className={Styles.stepWalletNoteTitle}>
                                            {walletAddress ? t('Кошелек обнаружин') : t('Кошелек не обнаружин')}
                                        </div>
                                        {walletAddress && (
                                            <div className={Styles.disconnectBtn} onClick={handleDisconnect}>
                                                {t('Выйти')}
                                            </div>
                                        )}
                                    </div>
                                    <div className={Styles.stepWalletNoteText}>
                                        {walletAddress
                                            ? `${walletAddress} ${t('установлен как ваш основной кошелёк и уже привязан к вашему аккаунту')}`
                                            : t('Пожалуйста, выберите кошелёк, чтобы продолжить регистрацию')}
                                    </div>
                                </div>

                                <div
                                    className={`${Styles.stepBtn} ${walletAddress && authStatus ? null : Styles.stepBtnDsb}`}
                                    onClick={() => {
                                        if (walletAddress && authStatus){
                                            setCurrentStep(2);
                                        }
                                    }}
                                >
                                    {t('Продолжить')}
                                </div>
                                <div className={Styles.honeeLogBtn} onClick={()=>{honeeLoginHandler()}}>{t('Вход через')} HoneeAI</div>
                            </div>
                        )}

                        {currentStep === 2 && (
                            <div className={`${Styles.stepWrap} ${Styles.step2}`}>
                                <div className={Styles.stepWrapTitle}>{t('Добро пожаловать в')} OMT Launchpad!</div>
                                <div className={Styles.stepWrapNote}>{t('Начните работу со своим аккаунтом')}</div>

                                <div className={`${Styles.inputWrap} ${userNameErr?.name ? Styles.inputWrapWithErr : null}`} >
                                    <div className={Styles.inputTitle}>
                                        {t('Выберите никнейм')} Choose a username <span>*</span>
                                    </div>
                                    <input
                                        type="text"
                                        placeholder={t('Ваш никнейм')}
                                        value={name}
                                        onChange={(e)=>{setName(e.target.value)}}
                                        onKeyDown={(event)=>{
                                            if (userNameErr?.name){
                                                delete userNameErr?.name;
                                                dispatch(setUserNameFailure(userNameErr));
                                            }
                                        }}
                                    />
                                    <div className={Styles.inputErr}>
                                        {userNameErr?.name ? userNameErr?.name : null}
                                    </div>
                                </div>
                                <div className={Styles.stepBtnRow}>
                                    <div className={Styles.backBtn} onClick={()=>{setCurrentStep(1)}}>{t('Назад')}</div>
                                    <div className={Styles.stepBtn} onClick={()=>{dispatch(setUserName({name: name}))}}>{t('Продолжить')}</div>
                                </div>
                            </div>
                        )}
                    </div>
                }
            </div>
        </section>
    );
};

export default RegPage;


